/* The furniture every panel is built from: the card with its heading and explanation, the
 * loading/failure wrapper, and the chart-or-table toggle.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Button, Card, SkeletonText } from '@nextleap-al/admin-ui';

import type { Query } from '../hooks/useApi';
import { useApp } from '../state/app';

interface PanelCardProps {
  title: string;
  /** The heading's wording in the simple view. */
  simpleTitle?: string;
  hint?: ReactNode;
  /** The explanation's wording in the simple view. */
  simpleHint?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function PanelCard({
  title,
  simpleTitle,
  hint,
  simpleHint,
  action,
  children,
  className,
}: PanelCardProps) {
  const { simple } = useApp();
  const heading = (simple && simpleTitle) || title;
  const explanation = simple && simpleHint !== undefined ? simpleHint : hint;

  return (
    <Card className={className}>
      <div className="mb-4 flex items-start gap-4">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-[var(--text-primary)]">{heading}</h2>
          {explanation && (
            <p className="mt-1 max-w-prose text-sm leading-relaxed text-[var(--text-secondary)]">
              {explanation}
            </p>
          )}
        </div>
        {action && <div className="ml-auto flex-none">{action}</div>}
      </div>
      {children}
    </Card>
  );
}

/**
 * Say, on the page, that a panel failed to load. A render that throws otherwise leaves an
 * empty card, which is indistinguishable from a panel that loaded and found nothing.
 */
export function PanelError({ error }: { error: Error }) {
  // The usual cause of a 404 here is a service started before the route existed.
  const stale = /→ 404$/.test(error.message)
    ? ' This endpoint does not exist in the running server — restart the analytics service to pick up new routes.'
    : '';
  return (
    <div className="rounded-[var(--radius-md)] border border-[var(--error)] bg-[var(--error-light)] px-4 py-3 text-sm text-[var(--text-primary)]">
      {`This panel could not load (${error.message}).${stale}`}
    </div>
  );
}

/**
 * False for the first quarter-second of a wait, so that nothing is drawn for a query that is
 * about to answer. A local one comes back inside that, and grey bars appearing and leaving
 * inside a blink read as the page stuttering; worse, a placeholder holds open a hole the
 * height of an answer that turns out to be a different height, and the cards below it move
 * twice. Past a quarter of a second the wait is real and worth saying.
 */
function useLateWait(ms = 260): boolean {
  const [late, setLate] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setLate(true), ms);
    return () => window.clearTimeout(timer);
  }, [ms]);
  return late;
}

/**
 * Loading, failed, or loaded, in that order of precedence. The previous answer stays on
 * screen while the next is fetched (see `useApi`), so the skeleton appears on first load only.
 */
export function Async<T>({
  query,
  shape = 'card',
  children,
}: {
  query: Query<T>;
  /** `page` where this query is the whole panel — which then waits as an empty well under
   *  the toolbar rather than as a stand-in for itself. Grey blocks in the shape of a page
   *  are still something rendered first and replaced a moment later, and no arrangement of
   *  them is right for every panel: the Overview is bands, the rest open on tiles. Nor is
   *  the wait announced — the bar that used to say so was itself a thing that appeared over
   *  an empty page and left again. The default is one card inside a page whose furniture is
   *  already drawn around it, where the card's own heading is on screen and only its body is
   *  pending. */
  shape?: 'card' | 'page';
  children: (data: T) => ReactNode;
}) {
  const late = useLateWait();

  if (query.error) return <PanelError error={query.error} />;
  if (query.data === undefined) {
    return shape === 'page' || !late ? null : <SkeletonText lines={4} />;
  }
  return <>{children(query.data)}</>;
}

/**
 * A chart and the same numbers as a table, one visible at a time. Two light-mode palette
 * slots sit below 3:1, so no chart here may be the only way to read its data.
 */
export function ChartOrTable({
  title,
  simpleTitle,
  hint,
  simpleHint,
  chart,
  table,
}: {
  title: string;
  simpleTitle?: string;
  hint?: ReactNode;
  simpleHint?: ReactNode;
  chart: ReactNode;
  table: ReactNode;
}) {
  const [showTable, setShowTable] = useState(false);
  return (
    <PanelCard
      title={title}
      simpleTitle={simpleTitle}
      hint={hint}
      simpleHint={simpleHint}
      action={
        <Button variant="ghost" size="sm" onClick={() => setShowTable((v) => !v)}>
          {showTable ? 'Chart view' : 'Table view'}
        </Button>
      }
    >
      {showTable ? table : chart}
    </PanelCard>
  );
}

/** Two cards side by side on a wide screen, stacked below it. */
export function Grid({ children }: { children: ReactNode }) {
  return <div className="grid gap-5 xl:grid-cols-2">{children}</div>;
}

/** The line a form writes its outcome on. */
export function ResultLine({ ok, children }: { ok: boolean; children: ReactNode }) {
  return (
    <p
      className={`mt-3 flex flex-wrap items-center gap-2 text-sm ${
        ok ? 'text-[var(--text-secondary)]' : 'text-[var(--error)]'
      }`}
    >
      <span aria-hidden="true">{ok ? '✓' : '✕'}</span>
      {children}
    </p>
  );
}
